import {
  FC,
  JSX,
  ReactElement,
  useEffect,
  useMemo,
  useState,
  useLayoutEffect,
  MutableRefObject,
} from 'react';
import { Table, TypeTable } from '../table/table.component';
import { Stack, SxProps, Theme, styled } from '@mui/material';
import { GridRowGroupingModel, useGridApiRef } from '@mui/x-data-grid-premium';
import { groupCellStyleWithToggle, hideUselessArgegationFields } from './functions/forStyle';
import { localstorage } from '../../helpers/storage/local.keys';
import { storageHelper } from '../../helpers/storage';
import { GroupButtons } from './components/groupButtons.component';
import { TopControls } from './components/topControls.component';
import { formColumnsAndFooterForGroupTable } from './functions/formColumnsAndFooterForGroupTable';
import { useGroupColumnWidth } from './hooks/useGroupColumnWidth';
import equal from 'fast-deep-equal/react';
import { MessageRotatorProps } from '../messageRotator/messageRotator.component';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { GridRowClassNameParams } from '@mui/x-data-grid/models/params';

export const GroupTable: FC<TypeGroupTable> = (props) => {
  const {
    isActive = true,
    columnVisibilityModel,
    viewCells,
    initButtonsSchema,
    initActiveGroup = [],
    sxMain,
    sxQuantityCircle,
    sxGroupCell,
    sxButtons,
    generateGroupCell,
    generateButtons,
    topControls: topControlsFunc,
    sxTopControls,
    groupLocalStorageKey,
    aggregationPossition = GroupTableAggregationPossitions.bottom,
    activeGroup,
    onGroup,
    getApiRef,
    groupViewRowAction,
    groupColWidth,
    ...restProps
  } = props;

  const apiRef = useGridApiRef();
  const agregationModel = props?.initialState?.aggregation?.model;
  const stylesForHideUselessArgegationFields = hideUselessArgegationFields(
    agregationModel,
    aggregationPossition,
  );

  const initRowGroupingModel = storageHelper.local.getItem(groupLocalStorageKey) || initActiveGroup;
  const [rowGroupingModel, setRowGroupingModel] =
    useState<GridRowGroupingModel>(initRowGroupingModel);

  const { columns, footer } = useMemo(
    () =>
      formColumnsAndFooterForGroupTable({
        children: props?.children,
        columnVisibilityModel,
        viewCells,
      }),
    [props?.children, columnVisibilityModel, viewCells, rowGroupingModel],
  );

  const groupColumnWidth = useGroupColumnWidth({
    rowGroupingModel,
    tableData: props?.data,
    columns,
  });

  useLayoutEffect(() => {
    if (activeGroup && !equal(activeGroup, rowGroupingModel)) {
      setRowGroupingModel(activeGroup);
      storageHelper.local.setItem(groupLocalStorageKey, activeGroup);
    }
  }, [activeGroup]);

  useEffect(() => {
    if (getApiRef) {
      getApiRef?.(apiRef);
    }
  }, []);

  useEffect(() => {
    // set Grouping Model to Table
    apiRef.current.setRowGroupingModel(rowGroupingModel);
    // hide column if we group by it
    columns?.forEach(({ field }) => {
      const isVisible = !rowGroupingModel.includes(field);
      apiRef.current.setColumnVisibility(field, isVisible);
    });
  }, [rowGroupingModel, apiRef, columns]);

  const mainStyles = {
    ...stylesForHideUselessArgegationFields,
    ...sxMain,
  } as SxProps<Theme>;

  return (
    <SWrapper sx={mainStyles}>
      <TopControls topControlsFunc={topControlsFunc} sxTopControls={sxTopControls} />
      <GroupButtons
        columns={columns}
        groupLocalStorageKey={groupLocalStorageKey}
        rowGroupingModel={rowGroupingModel}
        setRowGroupingModel={setRowGroupingModel}
        generateButtons={generateButtons}
        initButtonsSchema={initButtonsSchema}
        isActive={isActive}
        sxButtons={sxButtons}
        {...{ onGroup }}
      />
      <Table
        isGroupTable={true}
        externalApiRef={apiRef}
        columns={columns}
        footer={footer}
        groupingColDef={{
          width: groupColWidth || groupColumnWidth || 240,
          sortable: false,
          hideDescendantCount: true,
          renderCell: (params) =>
            groupCellStyleWithToggle({ params, sxQuantityCircle, sxGroupCell, generateGroupCell }),
        }}
        hidingColumnModel={{
          multiSelectFields: ['__row_group_by_columns_group__'],
        }}
        {...restProps}
        {...(groupViewRowAction && {
          getRowClassName: (params) => groupViewRowAction(params, apiRef.current),
        })}
      />
    </SWrapper>
  );
};

const SWrapper = styled(Stack)(({ theme }) => ({
  '[data-id^="auto-generated-row-"]': {
    backgroundColor: `${theme.palette.blue.light}`,
    cursor: 'unset',
  },
  '[data-id^="auto-generated-row-"].MuiDataGrid-row.Mui-selected': {
    backgroundColor: `${theme.palette.blue.light}!important`,
  },
  '[data-id^="auto-generated-row-"].MuiDataGrid-row:hover': {
    backgroundColor: `${theme.palette.blue.light}`,
  },
  '[data-id^="auto-generated-row-"] .MuiDataGrid-groupingCriteriaCell > span': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

interface IButtoSchema {
  field: string;
  headerName: string;
}

interface IGenerateGroupCellArgs {
  groupName?: string;
  groupMember: string;
  quantity: number;
}

interface IGenerateButtonsArgs {
  isActive: boolean;
  label?: string;
  onClick: () => void;
}

export enum GroupTableAggregationPossitions {
  bottom = 'bottom',
  middle = 'middle',
  both = 'both',
}

export type TypeGroupTable = TypeTable & {
  isActive?: boolean; // if u need to controll group mode
  initButtonsSchema?: IButtoSchema[]; // when u don't need buttons for all columns. Then u can choose them custom. Example when I need only group by column articleName: [{ field: 'articleName', headerName: 'Description' }]
  initActiveGroup?: string[]; // when u need start work with grouped table. Example when we need group by artice: ['article']
  activeGroup?: string[]; // when we want to change the grouping when the page is already rendered. Example when we need group by artice: ['article']
  sxMain?: SxProps<Theme>; // custom styles for the main container
  sxQuantityCircle?: SxProps<Theme>; // custom styles for circle with quantity of rows in the group (blue circle)
  sxGroupCell?: SxProps<Theme>; // custom styles for group cell
  sxButtons?: SxProps<Theme>; // custom styles for toggle buttons
  generateGroupCell?: (args: IGenerateGroupCellArgs) => JSX.Element; // when u need to make own component for group cell. Example: ({ groupName }) => <Stack> some text {groupName} just for example </Stack>
  generateButtons?: (args: IGenerateButtonsArgs) => JSX.Element; // when u need to make own component for toggle buttons. Example: ({ onClick, label }) => <button onClick={onClick}>{label}</button>
  sxTopControls?: SxProps<Theme>; // custom styles for components that above GroupTable
  children: ReactElement | ReactElement[];
  topControls?: () => ReactElement | ReactElement[]; // custom components that above GroupTable
  groupLocalStorageKey: localstorage; // need to save our rowGroupingModel to the cache
  aggregationPossition?: GroupTableAggregationPossitions; // when we use aggregation model we can manipulate align of result. Example: if we will use 'bottom' values will be in the footer. If we will use 'middle' values will be in the table rows.
  onGroup?: (activeGroup: string[]) => void;
  isLongLoading?: boolean;
  messagesForLongLoading?: MessageRotatorProps['uniqueMessages'];
  getApiRef?: (v?: MutableRefObject<GridApiPremium>) => void;
  groupViewRowAction?: (
    v: GridRowClassNameParams,
    apiRef?: MutableRefObject<GridApiPremium>['current'],
  ) => string;
  groupColWidth?: number;
};
